import { RenderingEngine, init as csInit, cache, Enums, setUseCPURendering, getShouldUseCPURendering } from "@cornerstonejs/core";
import { init as initTools } from "@cornerstonejs/tools";
import dicomImageLoader from "@cornerstonejs/dicom-image-loader";
import * as cornerstone from "@cornerstonejs/core";

let renderingEngine = null;
let initPromise = null;

function gpuIsAvailable() {
  if (typeof document === "undefined") {
    return false;
  }

  try {
    const canvas = document.createElement("canvas");

    const gl =
      canvas.getContext("webgl2", {
        failIfMajorPerformanceCaveat: false,
      }) ||
      canvas.getContext("webgl", {
        failIfMajorPerformanceCaveat: false,
      });

    if (!gl) {
      return false;
    }

    if (
      typeof gl.isContextLost === "function" &&
      gl.isContextLost()
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

export function attachWebGLRecovery(engine, viewportId) {
  if (!engine) return;

  const viewport = engine.getViewport?.(viewportId);

  if (!viewport) return;

  const canvas = viewport.getCanvas?.();

  if (!canvas) return;

  if (canvas.__pacsWebGLListenersAttached) {
    return;
  }

  canvas.__pacsWebGLListenersAttached = true;

  canvas.addEventListener(
    "webglcontextlost",
    (event) => {
      event.preventDefault();

      console.error(
        `[PACS] WebGL context lost: ${viewportId}`
      );
    },
    false
  );

  canvas.addEventListener(
    "webglcontextrestored",
    () => {
      console.warn(
        `[PACS] WebGL context restored: ${viewportId}`
      );
    },
    false
  );
}

export async function disableAllViewports() {
  const engine = renderingEngine;

  if (!engine) return;

  const viewports = engine.getViewports?.() || [];

  for (const viewport of viewports) {
    try {
      engine.disableElement(viewport.id);
    } catch (error) {
      console.debug(
        `[PACS] Could not disable viewport ${viewport.id}`,
        error
      );
    }
  }

  await waitForTwoFrames();
}

export async function destroyRenderingEngine() {
  if (!renderingEngine) {
    return;
  }

  const engine = renderingEngine;

  renderingEngine = null;

  try {
    const viewports = engine.getViewports?.() || [];

    for (const viewport of viewports) {
      try {
        engine.disableElement(viewport.id);
      } catch {
        // Ignore viewport teardown races.
      }
    }
  } catch {
    // Ignore.
  }

  await waitForTwoFrames();

  try {
    engine.destroy();
  } catch (error) {
    console.debug(
      "[PACS] RenderingEngine destroy failed:",
      error
    );
  }
}

export async function recycleRenderingEngine() {
  const engine = await getGlobalRenderingEngine();

  return engine;
}

function waitForTwoFrames() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

export async function getGlobalRenderingEngine() {
  if (!initPromise) {
    initPromise = (async () => {
      await csInit({
        rendering: {
          useCPURendering: false,

          preferSizeOverAccuracy: true,

          strictZSpacingForVolumeViewport: false,

          renderingEngineMode:
            Enums.RenderingEngineModeEnum.Tiled,

          /*
           * Keep ONE WebGL context.
           */
          webGlContextCount: 1,
        },
      });

      await initTools();

      cache.setMaxCacheSize(
        1536 * 1024 * 1024
      );

      dicomImageLoader.init({
        maxWebWorkers: Math.min(
          navigator.hardwareConcurrency || 4,
          4
        ),
        startWebWorker: true,
      });
      cornerstone.imageLoader.registerImageLoader(
        "wadouri",
        dicomImageLoader.wadouri.loadImage
      );
      if (getShouldUseCPURendering()) {
        setUseCPURendering(false, false);
      }

      console.log(
        "[PACS] Cornerstone initialized with GPU rendering"
      );
    })().catch((error) => {
     
      initPromise = null;
      throw error;
    });
  }

  await initPromise;

  if (!renderingEngine) {
    if (getShouldUseCPURendering()) {
      throw new Error(
        "Cornerstone is currently using CPU rendering. " +
          "Reload the viewer and try again."
      );
    }

    if (!gpuIsAvailable()) {
      throw new Error(
        "WebGL is unavailable in this browser."
      );
    }

    renderingEngine = new RenderingEngine(
      "GLOBAL_ENGINE"
    );

    console.log(
      "[PACS] Global RenderingEngine created"
    );
  }

  return renderingEngine;
}

export async function ensureGpuRenderingEngine() {
  const engine = await getGlobalRenderingEngine();

  if (getShouldUseCPURendering()) {
    throw new Error(
      "GPU rendering is unavailable. " +
        "Please reload the viewer and try 3-view again."
    );
  }

  if (!gpuIsAvailable()) {
    throw new Error(
      "WebGL is unavailable. " +
        "Please reload the viewer and try 3-view again."
    );
  }

  return engine;
}